<script setup lang="ts">
// Multi-axis radar / spider chart. `axes[]` defines the spokes
// (positional); `series[].values[]` aligns to the spokes by index.
// Each series renders as a filled polygon overlaid on the radar grid.
//
// Required `description` (enforced at the protocol type) renders as
// an info-tooltip on hover so viewers can read what each axis measures.
// Click datums carry { seriesName, axisIndex, axisName, value } so
// onClick handlers can drill into a specific (series, axis) cell.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { RadarChartSpec } from "../sbi/protocol";
import { PALETTE } from "../theme.ts";
import {
  axisLabelDefaults,
  chartChrome,
  legendDefaults,
  splitLineDefaults,
  tooltipDefaults,
} from "./echarts-base";
import { estimateLegendRows, NARROW } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = defineProps<{
  spec: RadarChartSpec;
  formats?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const indicators = computed(() =>
  props.spec.axes.map((a) => ({
    name: a.name,
    min: a.min,
    max: a.max,
  })),
);

const seriesData = computed(() =>
  props.spec.series.map((s, i) => ({
    name: s.name,
    value: [...s.values],
    itemStyle: { color: PALETTE[i % PALETTE.length] },
    areaStyle: { opacity: 0.15 },
  })),
);

// Narrow charts: a smaller polygon leaves room for the spoke names at
// the left and right poles, which also get a width cap so a long name
// truncates instead of clipping off the canvas. With a bottom legend
// the polygon moves up by the legend's rows.
const radarLayout = computed(() => {
  const w = ctx.value.width;
  const legend = props.spec.series.length > 1;
  const rows =
    legend && ctx.value.print && w > 0
      ? estimateLegendRows(props.spec.series.map((s) => s.name), w - 16)
      : 1;
  const reservePct = legend ? ((rows * 22) / height.value) * 100 : 0;
  return {
    radius: w > 0 && w < NARROW ? "55%" : "75%",
    center: ["50%", `${Math.round(50 - reservePct / 2)}%`],
    nameWidth: w > 0 ? Math.min(120, Math.max(48, Math.round(w * 0.22))) : undefined,
  };
});

const option = computed(() => ({
  ...chartChrome(ctx.value),
  tooltip: tooltipDefaults("item", ctx.value),
  legend: {
    ...legendDefaults(ctx.value),
    show: props.spec.series.length > 1,
    top: undefined,
    bottom: 0,
  },
  // Radar uses concentric polygons as gridlines plus radial spokes; the
  // axis-name labels are the spoke endpoints. All three need the mono
  // editorial treatment to match the cartesian charts.
  radar: {
    indicator: indicators.value,
    splitNumber: 4,
    radius: radarLayout.value.radius,
    center: radarLayout.value.center,
    axisName: {
      ...axisLabelDefaults(),
      ...(radarLayout.value.nameWidth
        ? { width: radarLayout.value.nameWidth, overflow: "truncate" as const }
        : {}),
    },
    splitLine: { ...splitLineDefaults() },
    axisLine: { lineStyle: splitLineDefaults().lineStyle },
  },
  series: [
    {
      type: "radar" as const,
      data: seriesData.value,
    },
  ],
}));

const height = computed(() =>
  resolveChartHeight("radar", props.spec.height),
);

function onClick(p: {
  seriesIndex?: number;
  data?: unknown;
  dataIndex?: number;
}) {
  if (!props.spec.id || !p) return;
  // ECharts radar fires click for the series polygon as a whole; we
  // surface the series + the full vector. Per-axis drill-down would
  // need a per-spoke hit test we don't currently install.
  const datum = (p.data ?? {}) as { name?: string; value?: number[] };
  emit("click-target", props.spec.id, {
    seriesName: datum.name ?? "",
    values: datum.value ?? [],
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
